import { useEffect, useRef, useState } from 'react';

import { SearchUtils } from '@spotify/confidence-core-react';
import {
  type PulseBlockFragment,
  type PulseBlockQuery,
  type PulseBlockQueryVariables,
  useOnPulseBlockUpdatedSubscription,
  usePulseBlockQuery,
  withStandardGetHook,
} from '@spotify/confidence-graphql';

import { isPulseBlockError, isPulseBlockPending } from '../utils/pulseDisplay';
import { PULSE_POLL_INTERVAL_MS, PULSE_POLL_TIMEOUT_MS } from './usePulse';
import type { PulseBlockRefreshState } from './usePulseDetail';

const usePulseBlockImpl = withStandardGetHook<
  PulseBlockQuery,
  PulseBlockFragment,
  'pulseBlock',
  PulseBlockQueryVariables
>(usePulseBlockQuery, 'pulseBlock');

type UsePulseBlockOptions = Parameters<typeof usePulseBlockImpl>[0] & {
  refreshCycleId?: number;
  refreshBaselineUpdateTime?: string;
  refreshShellUpdateTime?: string;
  refreshTimedOut?: boolean;
};
type UsePulseBlockResult = ReturnType<typeof usePulseBlockImpl> & {
  isPollingTimedOut: boolean;
  isRefreshing: boolean;
  refreshState?: PulseBlockRefreshState;
  refreshStateCycleId?: number;
};

type RefreshSnapshot = {
  baselineUpdateTime: string;
  cycleId: number;
  hydratedRequestSettled: boolean;
  state: PulseBlockRefreshState;
  terminalPulseBlock?: PulseBlockFragment;
};

export const usePulseBlock = (
  options: UsePulseBlockOptions,
): UsePulseBlockResult => {
  const [subscriptionCompleted, setSubscriptionCompleted] = useState(false);
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const [refreshSnapshot, setRefreshSnapshot] =
    useState<RefreshSnapshot | null>(null);
  const lastRefetchedUpdateTimeRef = useRef<string | null>(null);
  const result = usePulseBlockImpl({
    name: options.name,
    skip: options.skip,
    fetchPolicy: 'cache-and-network',
  });
  const { error, pulseBlock, refetch, startPolling, stopPolling } = result;
  const { error: subscriptionError } = useOnPulseBlockUpdatedSubscription({
    variables: {
      filter: `pulseBlock.name:${SearchUtils.escapeQuery(options.name)}`,
    },
    skip: options.skip || !options.name,
    fetchPolicy: 'no-cache',
    onData: () => {
      if (options.refreshCycleId !== undefined) return;
      void refetch().catch(() => undefined);
    },
    onComplete: () => setSubscriptionCompleted(true),
  });
  const pulseBlockRef = useRef(pulseBlock);
  pulseBlockRef.current = pulseBlock;
  const isPending = Boolean(pulseBlock && isPulseBlockPending(pulseBlock));
  const pendingVersion =
    pulseBlock && isPending
      ? `${pulseBlock.name}:${pulseBlock.updateTime}`
      : null;
  const isRefreshing = ['waiting', 'loading'].includes(
    refreshSnapshot?.state ?? '',
  );

  useEffect(() => {
    setSubscriptionCompleted(false);
  }, [options.name, options.skip]);

  useEffect(() => {
    const cycleId = options.refreshCycleId;
    const baselineUpdateTime = options.refreshBaselineUpdateTime;

    if (cycleId === undefined || baselineUpdateTime === undefined) {
      lastRefetchedUpdateTimeRef.current = null;
      setRefreshSnapshot(current =>
        current && ['failed', 'timedOut'].includes(current.state)
          ? current
          : null,
      );
      return;
    }

    setRefreshSnapshot(current => {
      if (current?.cycleId === cycleId) return current;
      lastRefetchedUpdateTimeRef.current = null;
      return {
        baselineUpdateTime,
        cycleId,
        hydratedRequestSettled: false,
        state: 'waiting',
      };
    });
  }, [options.refreshBaselineUpdateTime, options.refreshCycleId]);

  useEffect(() => {
    const shellUpdateTime = options.refreshShellUpdateTime;
    if (
      !refreshSnapshot ||
      refreshSnapshot.cycleId !== options.refreshCycleId ||
      refreshSnapshot.state !== 'waiting' ||
      !shellUpdateTime ||
      shellUpdateTime === refreshSnapshot.baselineUpdateTime ||
      shellUpdateTime === lastRefetchedUpdateTimeRef.current
    ) {
      return;
    }

    lastRefetchedUpdateTimeRef.current = shellUpdateTime;
    setIsPollingTimedOut(false);
    const requestedRefreshSnapshot: RefreshSnapshot = {
      ...refreshSnapshot,
      hydratedRequestSettled: false,
      state: 'loading',
    };
    setRefreshSnapshot(current =>
      current === refreshSnapshot ? requestedRefreshSnapshot : current,
    );
    void refetch()
      .then(() => {
        setRefreshSnapshot(current =>
          current === requestedRefreshSnapshot
            ? { ...current, hydratedRequestSettled: true }
            : current,
        );
      })
      .catch(() => {
        setRefreshSnapshot(current =>
          current === requestedRefreshSnapshot
            ? {
                ...current,
                state: 'failed',
                terminalPulseBlock: pulseBlockRef.current,
              }
            : current,
        );
      });
  }, [
    options.refreshCycleId,
    options.refreshShellUpdateTime,
    refetch,
    refreshSnapshot,
  ]);

  useEffect(() => {
    if (!options.refreshTimedOut) return;

    setRefreshSnapshot(current => {
      if (
        !current ||
        current.cycleId !== options.refreshCycleId ||
        ['succeeded', 'failed', 'timedOut'].includes(current.state)
      ) {
        return current;
      }
      return {
        ...current,
        state: 'timedOut',
        terminalPulseBlock: pulseBlockRef.current,
      };
    });
  }, [options.refreshCycleId, options.refreshTimedOut]);

  useEffect(() => {
    if (refreshSnapshot?.state !== 'loading') return;

    const refreshedBlockFailed = Boolean(
      pulseBlock &&
        pulseBlock.updateTime === options.refreshShellUpdateTime &&
        isPulseBlockError(pulseBlock),
    );
    if (
      (refreshSnapshot.hydratedRequestSettled && error) ||
      refreshedBlockFailed
    ) {
      setRefreshSnapshot(current =>
        current === refreshSnapshot
          ? { ...current, state: 'failed', terminalPulseBlock: pulseBlock }
          : current,
      );
      return;
    }

    if (isPollingTimedOut) {
      setRefreshSnapshot(current =>
        current === refreshSnapshot
          ? { ...current, state: 'timedOut', terminalPulseBlock: pulseBlock }
          : current,
      );
      return;
    }

    if (
      refreshSnapshot.hydratedRequestSettled &&
      pulseBlock &&
      pulseBlock.updateTime === options.refreshShellUpdateTime &&
      !isPending
    ) {
      setRefreshSnapshot(current =>
        current === refreshSnapshot
          ? { ...current, state: 'succeeded' }
          : current,
      );
    }
  }, [
    error,
    isPending,
    isPollingTimedOut,
    options.refreshShellUpdateTime,
    pulseBlock,
    refreshSnapshot,
  ]);

  useEffect(() => {
    if (
      !refreshSnapshot ||
      !['failed', 'timedOut'].includes(refreshSnapshot.state) ||
      !pulseBlock ||
      pulseBlock === refreshSnapshot.terminalPulseBlock ||
      pulseBlock.updateTime === refreshSnapshot.baselineUpdateTime ||
      error ||
      isPulseBlockError(pulseBlock)
    ) {
      return;
    }

    setRefreshSnapshot(current =>
      current === refreshSnapshot ? null : current,
    );
  }, [error, pulseBlock, refreshSnapshot]);

  useEffect(() => {
    setIsPollingTimedOut(false);
  }, [pendingVersion]);

  const hasPollingTimedOut =
    isPollingTimedOut || refreshSnapshot?.state === 'timedOut';

  useEffect(() => {
    if (!isPending || hasPollingTimedOut) return undefined;

    const timeoutId = window.setTimeout(() => {
      setIsPollingTimedOut(true);
    }, PULSE_POLL_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [hasPollingTimedOut, isPending, pendingVersion]);

  useEffect(() => {
    if (!isPending || hasPollingTimedOut) {
      stopPolling();
      return undefined;
    }

    startPolling(
      subscriptionError || subscriptionCompleted
        ? PULSE_POLL_INTERVAL_MS
        : PULSE_POLL_INTERVAL_MS * 6,
    );
    return () => stopPolling();
  }, [
    hasPollingTimedOut,
    isPending,
    pendingVersion,
    startPolling,
    stopPolling,
    subscriptionCompleted,
    subscriptionError,
  ]);

  return {
    ...result,
    isPollingTimedOut: hasPollingTimedOut,
    isRefreshing,
    refreshState: refreshSnapshot?.state,
    refreshStateCycleId: refreshSnapshot?.cycleId,
  };
};
